import { useEffect, useRef, useState } from 'react';

import { useStateContext } from '../../contexts/ContextProvider';
import type { HousekeepingRoomStatus } from '../../pages/housekeeping/types/housekeeping';
import { useChambresDeLHotel, useValiderControle } from '../api/useControles';
import Button from '../components/Button';
import EtatErreur from '../components/EtatErreur';
import IndicateurActualisation from '../components/IndicateurActualisation';
import Screen from '../components/Screen';
import Text from '../components/Text';
import { repartirControles } from '../helpers/controles';
import { echecSansDonnee } from '../helpers/requete';
import { t } from '../i18n/langue';
import { useTirerPourActualiser } from '../hooks/useTirerPourActualiser';
import EnteteApp from '../shell/EnteteApp';
import { useCoque } from '../shell/CoqueContext';

import CarteControle from './controles/CarteControle';
import FeuilleRefus from './controles/FeuilleRefus';

/**
 * « Contrôles » — la tournée de la gouvernante sur son téléphone (Beaucour, 14/10).
 *
 * Toutes les chambres de l'hôtel, pas seulement les siennes : d'abord le ménage
 * terminé qui attend son contrôle, puis les chambres propres qu'elle recontrôle
 * (une chambre peut servir la nuit sans être louée). Valider ou refuser avec un
 * motif ; le résultat se dit EN LIGNE, comme partout dans le mobile.
 */
export default function Controles() {
    const { ouvrirPanneau } = useCoque();
    const { currentFacility } = useStateContext();
    const { data, isLoading, isError, isFetching, refetch } = useChambresDeLHotel(currentFacility?.id ?? null);
    const valider = useValiderControle();
    const echec = echecSansDonnee({ isError, data });
    const { aControler, propres } = repartirControles(data ?? []);

    const [aRefuser, setARefuser] = useState<HousekeepingRoomStatus | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [alerte, setAlerte] = useState<string | null>(null);

    const refCorps = useRef<HTMLDivElement>(null);
    const [actualisationDemandee, setActualisationDemandee] = useState(false);
    const actualiser = () => {
        setActualisationDemandee(true);
        void refetch();
    };
    const { distance, pret } = useTirerPourActualiser(refCorps, actualiser);
    useEffect(() => {
        if (!isFetching) setActualisationDemandee(false);
    }, [isFetching]);

    const handleValider = (chambre: HousekeepingRoomStatus) => {
        setMessage(null);
        setAlerte(null);
        valider.mutate(
            { serviceId: chambre.service_id },
            {
                onSuccess: () => setMessage(t('controles.controlee', { chambre: chambre.room_name })),
                onError: (err) =>
                    setAlerte(err?.response?.data?.message || t('controles.validationEchec', { chambre: chambre.room_name })),
            },
        );
    };

    const handleRefusee = () => {
        setMessage(aRefuser ? t('controles.renvoyee', { chambre: aRefuser.room_name }) : null);
        setAlerte(null);
        setARefuser(null);
    };

    const section = (titre: string, chambres: HousekeepingRoomStatus[]) =>
        chambres.length > 0 && (
            <section className="flex flex-col gap-2">
                <Text variante="sousTitre" as="h2">
                    {t('controles.section', { titre, n: chambres.length })}
                </Text>
                {chambres.map((chambre) => (
                    <CarteControle
                        key={chambre.service_id}
                        chambre={chambre}
                        occupe={valider.isLoading}
                        onValider={() => handleValider(chambre)}
                        onRefuser={() => setARefuser(chambre)}
                    />
                ))}
            </section>
        );

    return (
        <Screen
            refCorps={refCorps}
            sansRebond
            entete={<EnteteApp titre={t('controles.titre')} onOuvrirPanneau={ouvrirPanneau} />}
        >
            <IndicateurActualisation distance={distance} pret={pret} enCours={actualisationDemandee && isFetching} />

            <div className="flex flex-col gap-5 px-4 py-4">
                {!isLoading && (
                    <div className="flex justify-end">
                        <Button libelle={t('controles.actualiser')} variante="lien" espacement="aucun" onPress={actualiser} />
                    </div>
                )}
                {message && (
                    <div role="status" className="rounded-xl bg-main-color-light px-3 py-2">
                        <Text variante="petit" ton="marque">
                            {message}
                        </Text>
                    </div>
                )}
                {alerte && (
                    <div role="alert">
                        <Text variante="petit" ton="danger">
                            {alerte}
                        </Text>
                    </div>
                )}
                {isLoading && (
                    <Text variante="petit" ton="attenue">
                        {t('commun.chargement')}
                    </Text>
                )}
                {echec && <EtatErreur onReessayer={() => void refetch()} />}
                {!isLoading && !echec && aControler.length + propres.length === 0 && (
                    <Text variante="petit" ton="attenue">
                        {t('controles.aucune')}
                    </Text>
                )}
                {section(t('controles.aControler'), aControler)}
                {section(t('controles.propres'), propres)}
            </div>

            {aRefuser && <FeuilleRefus chambre={aRefuser} onFermer={() => setARefuser(null)} onRefusee={handleRefusee} />}
        </Screen>
    );
}
